import { useEffect, useRef, useState } from "react";

const AUDIO_THRESHOLD = 10;
const CHECK_INTERVAL = 200;

const useAudioDetection = (stream) => {
  const audioDetectedRef = useRef(false);
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);

  const [hasDetectedAudio, setHasDetectedAudio] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  const startAudioDetection = () => {
    if (!stream) return;

    audioDetectedRef.current = false;
    setHasDetectedAudio(false);

    const audioContext = new AudioContext();

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();

    analyser.fftSize = 256;

    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);

    audioContextRef.current = audioContext;

    intervalRef.current = setInterval(() => {
      analyser.getByteFrequencyData(data);

      const average = data.reduce((sum, value) => sum + value, 0) / data.length;

      setAudioLevel(average);

      const hasAudio = average > AUDIO_THRESHOLD;

      if (hasAudio) {
        audioDetectedRef.current = true;
        setHasDetectedAudio(true);
      }
    }, CHECK_INTERVAL);
  };

  const stopAudioDetection = async () => {
    clearInterval(intervalRef.current);
    setAudioLevel(0);

    const audioContext = audioContextRef.current;

    audioContextRef.current = null;

    if (!audioContext) return;

    try {
      if (audioContext.state !== "closed") {
        await audioContext.close();
      }
    } catch (error) {
      if (error.name !== "InvalidStateError") {
        throw error;
      }
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);

      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
    };
  }, []);

  return {
    startAudioDetection,
    stopAudioDetection,
    hasDetectedAudio,
    audioLevel,
    audioDetectedRef,
  };
};

export default useAudioDetection;
